import type {
  AutoAgentMonitorOwnerSection,
  AutoAgentMonitorSortDirection,
  AutoAgentMonitorTicket,
} from "../../types/integration";
import { RESOLVER_STATE_LABELS } from "../../shared/components/resolverStateStyle";

export type OwnerCounts = Record<AutoAgentMonitorOwnerSection, number>;

export interface OwnerGroup {
  key: AutoAgentMonitorOwnerSection;
  label: string;
  /** One line under the label saying what the group asks of the technician. */
  hint?: string;
  /** Needs-you items are oldest first, so the longest wait is on top. */
  sortDirection: AutoAgentMonitorSortDirection;
  defaultCollapsed: boolean;
  attention: boolean;
  pageSize: number;
}

export const OWNER_GROUPS: OwnerGroup[] = [
  { key: "needs_you", label: "Needs technician", sortDirection: "asc", defaultCollapsed: false, attention: true, pageSize: 25 },
  { key: "working", label: "Theo is working", sortDirection: "desc", defaultCollapsed: false, attention: false, pageSize: 25 },
  {
    key: "done_open",
    label: "Done, PSA still open",
    hint: "Theo finished. Close these out in the PSA.",
    sortDirection: "desc",
    defaultCollapsed: false,
    attention: false,
    pageSize: 10,
  },
  { key: "closed", label: "Closed", sortDirection: "desc", defaultCollapsed: true, attention: false, pageSize: 10 },
];

export const EMPTY_OWNER_COUNTS: OwnerCounts = { needs_you: 0, working: 0, done_open: 0, closed: 0 };

const ACTIVE_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;

/** Poll fast only while a run is in flight; idle pages still pick up new runs, slowly. */
export function livePollDelayMs(counts: OwnerCounts | null | undefined): number {
  return counts && counts.working > 0 ? ACTIVE_POLL_MS : IDLE_POLL_MS;
}

/** Horizon returns MySQL timestamps without a zone; they are UTC. */
export function parseApiTimestamp(value?: string | null): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const date = new Date(hasTimezone ? trimmed : `${trimmed.replace(" ", "T")}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatElapsed(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export function activityLabel(row: AutoAgentMonitorTicket, nowMs: number): string {
  if (row.resolver_state === "running") {
    const started = parseApiTimestamp(row.current_turn_started_at);
    if (started) return `running ${formatElapsed(nowMs - started.getTime())}`;
  }
  const last = parseApiTimestamp(row.last_activity_at || row.updated_at);
  if (!last) return "—";
  const elapsed = formatElapsed(nowMs - last.getTime());
  return elapsed === "just now" ? elapsed : `${elapsed} ago`;
}

export function resolverStateText(row: AutoAgentMonitorTicket): string {
  const context = row.resolver_context;
  if (row.resolver_state === "waiting" && context?.on) {
    const reason = context.kind === "escalation" ? context.reason === "timeout" ? " · timed out" : " · escalated" : "";
    return `Waiting for ${context.on === "system" ? "system" : "technician"}${reason}`;
  }
  return RESOLVER_STATE_LABELS[row.resolver_state] || row.resolver_state || "—";
}
